/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { IGeoRegion } from "../../../store/api/admin/types";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { isLoadingStatusSelector } from "../../../store/helpers/helperSelectors";
import { ISiteGroup } from "../../../store/api/devices/types";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { RadixSelectControl } from "../../ui/radix-select";
import { Loader2 } from "lucide-react";

interface IMigrateSiteToRegionProps {
    currentRegion: IGeoRegion;
    siteGroupId: string;
    setSiteGroupId: (value: React.SetStateAction<string>) => void;
    startMigrationHandler?: React.MouseEventHandler<HTMLButtonElement> | undefined;
}

const MigrateSiteToRegion: React.FC<IMigrateSiteToRegionProps> = ({ currentRegion, siteGroupId, setSiteGroupId, startMigrationHandler }) => {
    const dispatch = useStoreDispatch();
    const isLoadingStatus = useSelector(isLoadingStatusSelector);
    const { geoRegions } = useRegionDisplay();
    const isMigrationInProgress = isLoadingStatus["migrateStatus"];
    const [selectedRegionKey, setSelectedRegionKey] = useState<string>("");

    useEffect(() => {
        if (selectedRegionKey) return;
        const defaultRegionKey =
            (currentRegion?.objectKey && geoRegions?.some((region) => region.objectKey === currentRegion.objectKey && (region.siteGroups?.length ?? 0) > 0))
                ? currentRegion.objectKey
                : (geoRegions?.find((region) => (region.siteGroups?.length ?? 0) > 0)?.objectKey || "");
        setSelectedRegionKey(defaultRegionKey);
    }, [selectedRegionKey, currentRegion, geoRegions]);

    useEffect(() => {
        if (!selectedRegionKey) {
            if (siteGroupId) setSiteGroupId("");
            return;
        }

        const selectedRegion = geoRegions?.find((region) => region.objectKey === selectedRegionKey);
        const selectedSiteGroups: ISiteGroup[] = selectedRegion?.siteGroups ?? [];
        const isSiteInSelectedRegion = selectedSiteGroups.some((site) => site.objectKey === siteGroupId);

        if (!isSiteInSelectedRegion && siteGroupId) {
            setSiteGroupId("");
        }
    }, [selectedRegionKey, geoRegions, siteGroupId, setSiteGroupId]);

    const handleClose = () => {
        dispatch(setBasicDialogOpenFor(undefined));
    };

    const handleRegionChange = (value: string) => {
        setSelectedRegionKey(value || "");
        setSiteGroupId("");
    };

    const selectedRegion = geoRegions?.find((region) => region.objectKey === selectedRegionKey);
    const filteredSiteGroups: ISiteGroup[] = selectedRegion?.siteGroups ?? [];

    const regionOptions = (geoRegions || [])
        .filter((region) => (region.siteGroups?.length ?? 0) > 0)
        .map((region) => ({
            label: region.name,
            value: region.objectKey,
        }));

    const siteGroupOptions = filteredSiteGroups.map((site: ISiteGroup) => ({
        label: site.name,
        value: site.objectKey,
    }));

    const footer = (
        <>
            <Button variant="outline" onClick={handleClose} disabled={isMigrationInProgress}>
                Cancel
            </Button>
            <Button
                disabled={isMigrationInProgress || !siteGroupId}
                onClick={startMigrationHandler}
            >
                {isMigrationInProgress && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Start Migration
            </Button>
        </>
    );

    return (
        <Sheet open={true} onClose={handleClose} title="Migrate Devices to Site Group" footer={footer} width="420px">
            <form autoComplete="off">
                <div className="space-y-5 pt-2">
                    <div className="space-y-2">
                        <Label>Regions</Label>
                        <RadixSelectControl
                            options={regionOptions}
                            value={selectedRegionKey || ""}
                            onValueChange={handleRegionChange}
                            placeholder="Select a region"
                            autoFocus={true}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Site Groups</Label>
                        <RadixSelectControl
                            options={siteGroupOptions}
                            value={siteGroupId || ""}
                            onValueChange={(value) => setSiteGroupId(value)}
                            placeholder="Select a site group"
                        />
                    </div>
                </div>
            </form>
        </Sheet>
    )
}

export default MigrateSiteToRegion;